import { resolveExecutor, getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import type { ResolveLocationInput, ResolveLocationResult } from '../../types';
import { findLocationByCustomerRef, findLocationByAlias, findLocationByPlaceId, findLocationsNearby } from '../location-lookup'
import { geocodeAddress } from '../geocoding'
import { scoreCandidates } from './score-candidates'
import { createLocation } from './createLocation'
import { recordInputAliasAsync } from './recordInputAlias'
import { generateOperationId, scopeLogger, elapsed } from '../../logging'

export interface ResolveLocationParams extends ResolveLocationInput {
  executor?: GqlExecutor
  /** Optional correlation ID for log tracing across services */
  correlationId?: string
}

/**
 * Smart location resolution with deduplication.
 *
 * @see location-matching-algorithm.md
 */
export async function resolveLocation(params: ResolveLocationParams): Promise<ResolveLocationResult> {
  const executor = resolveExecutor(params.executor);
  const autoResolveThreshold = params.resolution?.autoResolveThreshold ?? 1.0
  const autoCreate = params.resolution?.autoCreate ?? true
  const lookupOnly = params.resolution?.lookupOnly ?? false

  const log = scopeLogger(getLogger(), {
    operationId: generateOperationId(),
    operation: 'resolveLocation',
    customerId: params.customerId,
    ...(params.correlationId && { correlationId: params.correlationId }),
  })
  const startedAt = Date.now()

  log.info('resolveLocation.start', { address: params.address })

  // ═══════════════════════════════════════════════════════════════════
  // FAST PATH: Check customer_ref and input_alias before geocoding
  // ═══════════════════════════════════════════════════════════════════

  // Priority 1: Customer reference (if provided)
  if (params.customerRef) {
    const refMatch = await findLocationByCustomerRef({
      customerId: params.customerId,
      customerRef: params.customerRef,
      executor,
    })
    if (refMatch) {
      log.info('resolveLocation.resolved', { method: 'customer_ref', locationId: refMatch.id, elapsedMs: elapsed(startedAt) })
      return {
        resolved: true,
        location: refMatch,
        matchMethod: 'customer_ref',
        matchScore: 1.0,
      }
    }
  }

  // Priority 2: Input alias (previously-seen input string)
  const aliasMatch = await findLocationByAlias({
    customerId: params.customerId,
    input: params.address,
    executor,
  })
  if (aliasMatch) {
    log.info('resolveLocation.resolved', { method: 'input_alias', locationId: aliasMatch.id, elapsedMs: elapsed(startedAt) })
    return {
      resolved: true,
      location: aliasMatch,
      matchMethod: 'input_alias',
      matchScore: 1.0,
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // STANDARD PATH: Geocode and search
  // ═══════════════════════════════════════════════════════════════════
  // Note: geocodeAddress checks the global geocode cache internally
  // before calling Google API

  const geocodeResult = await geocodeAddress(params.address, executor, params.placeId)

  // Transform geocode result to match the expected return type
  const geocoded = {
    streetAddress: geocodeResult.streetAddress,
    formattedAddress: geocodeResult.formattedAddress,
    lat: geocodeResult.latitude,
    lng: geocodeResult.longitude,
    placeId: geocodeResult.placeId,
    plusCode: geocodeResult.plusCode,
  }

  // ═══════════════════════════════════════════════════════════════════
  // PARALLEL LOOKUPS: Place ID and proximity search run concurrently
  // ═══════════════════════════════════════════════════════════════════

  const [placeIdMatch, proximityCandidates] = await Promise.all([
    findLocationByPlaceId({
      customerId: params.customerId,
      placeId: geocodeResult.placeId,
      executor,
    }),
    findLocationsNearby({
      customerId: params.customerId,
      lat: geocodeResult.latitude,
      lng: geocodeResult.longitude,
      executor,
    }),
  ])

  // If place_id matched, return immediately
  if (placeIdMatch) {
    recordInputAliasAsync({
      locationId: placeIdMatch.id,
      customerId: params.customerId,
      input: params.address,
      executor,
    })
    log.info('resolveLocation.resolved', { method: 'place_id', locationId: placeIdMatch.id, elapsedMs: elapsed(startedAt) })
    return {
      resolved: true,
      location: placeIdMatch,
      matchMethod: 'place_id',
      matchScore: 1.0,
      geocoded,
    }
  }

  // Score proximity candidates
  const scoredCandidates = scoreCandidates(
    proximityCandidates,
    {
      name: params.name,
      streetAddress: geocodeResult.streetAddress,
      addressLineTwo: params.addressLineTwo,
    }
  )

  const bestCandidate = scoredCandidates[0]

  // Check if best candidate meets auto-resolve threshold
  if (bestCandidate && bestCandidate.score >= autoResolveThreshold) {
    recordInputAliasAsync({
      locationId: bestCandidate.location.id,
      customerId: params.customerId,
      input: params.address,
      executor,
    })
    log.info('resolveLocation.resolved', {
      method: 'proximity',
      locationId: bestCandidate.location.id,
      score: bestCandidate.score,
      candidateCount: scoredCandidates.length,
      elapsedMs: elapsed(startedAt),
    })
    return {
      resolved: true,
      location: bestCandidate.location,
      matchMethod: 'proximity',
      matchScore: bestCandidate.score,
      candidates: scoredCandidates,
      geocoded,
    }
  }

  // No candidates or below threshold - check if we should auto-create
  if (scoredCandidates.length === 0 && autoCreate && !lookupOnly) {
    const newLocation = await createLocation({
      customerId: params.customerId,
      name: params.name ?? geocodeResult.formattedAddress,
      address: geocodeResult.formattedAddress,
      addressLineOne: geocodeResult.streetAddress,
      addressLineTwo: params.addressLineTwo,
      city: geocodeResult.components.city,
      state: geocodeResult.components.state,
      zipCode: geocodeResult.components.postalCode,
      latitude: geocodeResult.latitude,
      longitude: geocodeResult.longitude,
      placeId: geocodeResult.placeId,
      plusCode: geocodeResult.plusCode,
      coordinatesSource: 'geocoded',
      placeIdAuthoritative: true,
      executor,
    })

    recordInputAliasAsync({
      locationId: newLocation.id,
      customerId: params.customerId,
      input: params.address,
      executor,
    })

    log.info('resolveLocation.created', { locationId: newLocation.id, elapsedMs: elapsed(startedAt) })
    return {
      resolved: true,
      location: newLocation,
      created: true,
      geocoded,
    }
  }

  // Return candidates for caller decision
  log.warn('resolveLocation.unresolved', {
    candidateCount: scoredCandidates.length,
    bestScore: bestCandidate?.score ?? null,
    elapsedMs: elapsed(startedAt),
  })
  return {
    resolved: false,
    candidates: scoredCandidates,
    geocoded,
  }
}
